import type { HomeyAPI, Zone } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type ZoneProps = {
  [K in keyof Zone as Zone[K] extends Function ? never : K]: Zone[K];
};

export type ZonesData = { [key: string]: Zone };
export type ZonesById = { [key: string]: ZoneProps };

interface State {
  data: ZonesData | null;
  byId: ZonesById | null;
  rootZoneId: string | null;
  isLoading: boolean;
  error: unknown;
}

export type ZonesStoreState = State;

export class ZonesStore extends BaseApiStore<State, Zone> {
  constructor() {
    super('zones');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      rootZoneId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.zones, reference: this });

      if (abortController.signal.aborted) return;

      const zonesPromise = api.zones.getZones({ $cache: cache });

      const [zonesData] = await Promise.all([zonesPromise]);

      if (abortController.signal.aborted) return;

      const zones = Object.values(zonesData);

      const data: ZonesData = {};
      const byId: ZonesById = {};

      for (const zoneInstance of zones) {
        const zoneReference = { ...zoneInstance };

        data[zoneReference.id] = zoneInstance;
        byId[zoneReference.id] = zoneReference;
      }

      const rootZoneId = this.getRootZoneId(zones);

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        rootZoneId: rootZoneId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  getRootZoneId(zones: Array<Zone>) {
    for (const zone of zones) {
      if (zone.parent == null) {
        return zone.id;
      }
    }

    return null;
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.zones, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.zones.on('zone.create', this.handleCreate);
    api.zones.on('zone.update', this.handleUpdate);
    api.zones.on('zone.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.zones.off('zone.create', this.handleCreate);
    api.zones.off('zone.update', this.handleUpdate);
    api.zones.off('zone.delete', this.handleDelete);
  }

  getInstanceFromCache(zone: { id: string }) {
    return this.getCache()[zone.id];
  }

  getCache(): { [key: string]: Zone } {
    const api = this.getApi();
    const cache = api.zones.__cache['zone'];
    return cache;
  }

  handleCreate = (createdZone: Zone) => {
    this.DEBUG && console.info(`create:zone:${createdZone.id}`);
    const state = this.get();
    const zoneInstance = this.getInstanceFromCache(createdZone);

    if (zoneInstance == null) return;

    const zoneReference = { ...zoneInstance };

    this.set({
      data: {
        ...state.data,
        [createdZone.id]: zoneInstance,
      },
      byId: {
        ...state.byId,
        [createdZone.id]: zoneReference,
      },
    });
  };

  handleUpdate = (updatedZone: Zone) => {
    this.DEBUG && console.info(`update:zone:${updatedZone.id}`);

    this.addToQueue(updatedZone.id, updatedZone);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedZone] of Object.entries(updateQueue)) {
        const zoneInstance = this.getInstanceFromCache(queuedZone);

        if (state.data == null) {
          this.DEBUG && console.info(`update:zone:missing-data`);
          continue;
        }

        if (zoneInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:zone:unknown:${id}`);
          continue;
        }

        const zoneReference = { ...zoneInstance };

        nextById[id] = zoneReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:zone:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedZone: Zone) => {
    this.DEBUG && console.info(`delete:zone:${deletedZone.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:zone:missing-data`);
      return;
    }

    const zoneInstance = state.data[deletedZone.id];

    if (zoneInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[zoneInstance.id];
    delete nextById[zoneInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const zones = Object.values(cache ?? {});
    const data: ZonesData = {};
    const byId: ZonesById = {};

    for (const zoneInstance of zones) {
      const zoneReference = { ...zoneInstance };
      data[zoneReference.id] = zoneInstance;
      byId[zoneReference.id] = zoneReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const zonesStore = new ZonesStore();
